/**
 * Link Dialog Component
 * Dialog for creating and editing links in the editor
 */

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'

export interface LinkDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (url: string, text: string) => void
  onRemove?: () => void
  initialUrl?: string
  initialText?: string
  title?: string
  mode?: 'create' | 'edit'
}

/**
 * Dialog for creating and editing links
 */
export const LinkDialog: React.FC<LinkDialogProps> = ({
  open,
  onClose,
  onConfirm,
  onRemove,
  initialUrl = '',
  initialText = '',
  title,
  mode = 'create',
}) => {
  const dialogTitle = title || (mode === 'edit' ? 'Edit Link' : 'Insert Link')
  const [url, setUrl] = useState('')
  const [text, setText] = useState('')
  const [urlError, setUrlError] = useState('')

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setUrl(initialUrl)
      setText(initialText)
      setUrlError('')
    } else {
      setUrl('')
      setText('')
      setUrlError('')
    }
  }, [open, initialUrl, initialText])

  // Validate URL
  const validateUrl = (url: string): boolean => {
    if (!url.trim()) {
      setUrlError('URL is required')
      return false
    }

    try {
      // Try to create a URL object - this will throw if invalid
      new URL(url)
      setUrlError('')
      return true
    } catch {
      // If URL constructor fails, try adding https:// prefix
      try {
        new URL(`https://${url}`)
        setUrlError('')
        return true
      } catch {
        setUrlError('Please enter a valid URL')
        return false
      }
    }
  }

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = event.target.value
    setUrl(newUrl)

    // Clear error when user starts typing
    if (urlError) {
      setUrlError('')
    }
  }

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setText(event.target.value)
  }

  const handleConfirm = () => {
    if (!validateUrl(url)) {
      return
    }

    // Normalize URL (add https:// if missing)
    let normalizedUrl = url.trim()
    if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
      normalizedUrl = `https://${normalizedUrl}`
    }

    const linkText = text.trim() || normalizedUrl
    onConfirm(normalizedUrl, linkText)
    onClose()
  }

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleConfirm()
    }
  }

  const handleRemove = () => {
    if (onRemove) {
      onRemove()
      onClose()
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth disableRestoreFocus>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="URL"
            value={url}
            onChange={handleUrlChange}
            onKeyPress={handleKeyPress}
            error={!!urlError}
            helperText={urlError || 'Enter the URL you want to link to'}
            placeholder="https://example.com"
            fullWidth
            autoFocus
            variant="outlined"
          />
          <TextField
            label="Link Text (optional)"
            value={text}
            onChange={handleTextChange}
            onKeyPress={handleKeyPress}
            helperText="Text to display for the link (defaults to URL if empty)"
            placeholder="Click here"
            fullWidth
            variant="outlined"
          />
          {text && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                Preview:{' '}
                <a
                  href={url.startsWith('http') ? url : `https://${url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--editor-link)', textDecoration: 'underline' }}
                >
                  {text}
                </a>
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {mode === 'edit' && onRemove && (
          <Button onClick={handleRemove} color="error">
            Remove Link
          </Button>
        )}
        <Button onClick={handleConfirm} variant="contained" disabled={!url.trim()}>
          {mode === 'edit' ? 'Update Link' : 'Insert Link'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default LinkDialog
